/**
 * map-track.js
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Box } from '@mui/material';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';
import { TRACK_TYPE } from '../tracking-common';
import { gmapCoords } from './map-common';
import { MapContext } from './map-context';




export const TRACK_COLOR = {
  [TRACK_TYPE.DEPLOYMENT]: '#2680c0',
  [TRACK_TYPE.LEAD]: '#d64343',
  [TRACK_TYPE.FOLLOW]: '#2680c0',
  [TRACK_TYPE.MULTI_DEPLOYMENT]: [
    '#2680c0',
    '#f0b428',
    '#d64343',
    '#27aa57',
    '#b12bac',
    '#ed8402',
    '#00e0dd',
    '#ff38bd',
    '#1506bc',
    '#00cc25'
  ]
};


const PATH_BORDER_LIGHT = 'hsla(0, 0%, 100%, 0.6)';
const PATH_BORDER_DARK = 'hsla(210, 24%, 16%, 0.3)';
const PIN_MARKER_COLOR = '#f0c44e';


const MARKER_TYPE = {
  START: 'start',
  END: 'end',
  MARKER: 'marker',
  PHOTO: 'photo',
};


const TRACK_MARKER_ENDPOINT = {
  [TRACK_TYPE.DEPLOYMENT]: {
    [MARKER_TYPE.START]: '/asset/home-marker',
    [MARKER_TYPE.END]: '/asset/dog-marker',
  },
  [TRACK_TYPE.MULTI_DEPLOYMENT]: {
    [MARKER_TYPE.START]: '/asset/home-marker',
    [MARKER_TYPE.END]: '/asset/dog-marker',
  },
  [TRACK_TYPE.LEAD]: {
    [MARKER_TYPE.START]: '/asset/home-marker',
    [MARKER_TYPE.END]: '/asset/run-marker'
  },
  [TRACK_TYPE.FOLLOW]: {
    [MARKER_TYPE.START]: '/asset/home-marker',
    [MARKER_TYPE.END]: '/asset/dog-marker'
  },
  [MARKER_TYPE.MARKER]: '/asset/star-marker',
  [MARKER_TYPE.PHOTO]: '/asset/camera-marker',
};


const TRACK_MARKER_SIZE = {
  DEFAULT: '50px',
  LARGE: '65px',
}


const TRACK_MARKER_TRANSLATE_Y = {
  DEFAULT: '9px',
  LARGE: '12px',
}


// create marker helper
const markerContent = (trackType, markerType, color, size) => {
  const markerElement = document.createElement('img');
  markerElement.style.maxHeight = TRACK_MARKER_SIZE[size === 'large' ? 'LARGE' : 'DEFAULT'];
  markerElement.style.transform = `translateY(${TRACK_MARKER_TRANSLATE_Y[size === 'large' ? 'LARGE' : 'DEFAULT']})`;
  markerElement.style.transition = `100ms ease-in-out`;
  const endpoint = markerType === MARKER_TYPE.MARKER || markerType === MARKER_TYPE.PHOTO
    ? TRACK_MARKER_ENDPOINT[markerType]
    : TRACK_MARKER_ENDPOINT[trackType][markerType];
  markerElement.src = `${process.env.REACT_APP_TRACKING_HOST}${endpoint}?color=${color.replace('#', '')}`;
  return markerElement;
}


const DebugDetailMarker = (props) => {
  const { children } = props;

  return (
    <Box sx={{
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      color: 'white',
      padding: '3px',
      borderRadius: '4px',
    }}>
      {children}
    </Box>
  );
}


export default function MapTrack(props) {
  const { 
    id,
    type, 
    points, 
    markers, 
    color, 
    isActive, 
    isHidden,
    isSelected, 
    displayedPointProperty, 
    index 
  } = props;
  const { activeTrack } = React.useContext(LiveTrackingContext);
  const { mapApi, map, selectedMarkerUuid, selectMarker } = React.useContext(MapContext);
  const [doRedraw, setDoRedraw] = React.useState(1);
  const elements = React.useRef();
  const pinMarkers = React.useRef({});
  const debugMarkers = React.useRef([]);
  const strokeWeightBase = isSelected ? 10 : 8;
  const isActiveTrack = id === activeTrack?.id;
  const zIndexBase = isActive
    ? 100
    : type === TRACK_TYPE.FOLLOW
      ? index + 10 
      : index + 0;


  // sets map for all map track parts. used to show / hide / cleanup.
  const setMap = React.useCallback((map) => {
    if (!elements.current) {
      return;
    }

    elements.current.path.setMap(map);
    elements.current.pathBorderLight.setMap(map);
    elements.current.pathBorderDark.setMap(map);
    elements.current.start.map = map;
    if (elements.current.end) {
      elements.current.end.map = map;
    }

    for (const [, pinMarker] of Object.entries(pinMarkers.current)) {
      pinMarker.map = map;
    }

    debugMarkers.current.forEach(mObj => {
      mObj.marker.map = map;
    });
  }, []);


  // track path drawing
  React.useEffect(() => {
    const strokeWeightLight = strokeWeightBase + 3;
    const strokeWeightDark = strokeWeightBase + 6;

    // create track helper
    const createTrack = () => {

      // PATH BORDERS
      const pathBorderLight = new mapApi.Line({
        map,
        strokeColor: PATH_BORDER_LIGHT,
        strokeOpacity: 1,
        strokeWeight: strokeWeightLight,
        zIndex: zIndexBase,
      });
      const pathBorderDark = new mapApi.Line({
        map,
        strokeColor: PATH_BORDER_DARK,
        strokeOpacity: 1,
        strokeWeight: strokeWeightDark,
        zIndex: zIndexBase + 1,
      });

      // PATH
      // const arrow = {
      //   path: 'M 0,0 -1.1,2 0,1.5 1.1,2 z',
      //   strokeWeight: 1.5,
      //   strokeColor: shadowColor,
      //   fillColor: color,
      //   fillOpacity: 1
      // };
      const arrow = {
        path: 'M -0.6,1 0,0 0.6,1',
        anchor: new mapApi.Point(0, 0),
        strokeWeight: 2,
        strokeColor: 'hsla(210, 24%, 16%, 0.6)',
        fillOpacity: 0
      };
      const path = new mapApi.Line({
        map,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: strokeWeightBase, 
        zIndex: zIndexBase + 2,
        icons: [{
          icon: arrow,
          repeat: '40px'
        }]
      });

      // START MARKER
      const start = new mapApi.Marker({
        map,
        title: 'Start Marker',
        zIndex: zIndexBase + 2,
        content: markerContent(type, MARKER_TYPE.START, color),
      });

      // END MARKER
      const end = !isActiveTrack 
        ? new mapApi.Marker({
          map,
          title: 'End Marker',
          zIndex: zIndexBase + 3,
          content: markerContent(type, MARKER_TYPE.END, color),
        })
        : null;

      // register event handlers
      const zoomListener = mapApi.Event.addListener(map, 'zoom_changed', () => {
        setDoRedraw(p => p + 1);  
      });

      // forward path click events to map
      const clickListener = path.addListener("click", (e) => {
        mapApi.Event.trigger(map, 'click', e);
      });

      // ALL TOGETHER NOW
      return {
        pathBorderLight,
        pathBorderDark,
        path,
        start,
        end,
        zoomListener,
        clickListener,
      };
    }


    // update path helper
    const drawPath = () => {
      if (points.length > 0) {
        elements.current.start.position = gmapCoords(points[0]);
      } 
      
      if (points.length > 1) {
        if (elements.current.end) {
          elements.current.end.position = gmapCoords(points[points.length - 1]);
        }

        elements.current.path.setOptions({ strokeWeight: strokeWeightBase });
        elements.current.pathBorderLight.setOptions({ strokeWeight: strokeWeightLight });
        elements.current.pathBorderDark.setOptions({ strokeWeight: strokeWeightDark });

        // while this doesn't appear to suffer from performance issues even with many points, consider improving efficiency:
        // - store context points in gmap format so we aren't reformatting the array for every point change.
        // - efficient algo:
        //    - elements.getPath() returns a mutable array, can simply append the diff to it.
        //    - as filtering algo works, expectation is that only some few at the end of locations in list will 
        //      deviate from current list. 
        //    - use backwards scan algo from new point list end until they match to determine diff.
        //    - append to list instead of setting whole path every time?

        if (map.getProjection()) {
          elements.current.pathBorderLight.setPath(makeShadowPoints(points));
          elements.current.pathBorderDark.setPath(makeShadowPoints(points));
          elements.current.path.setPath(points.map(point => gmapCoords(point)));
        }
      }
    }


    // make shadow points helper
    const makeShadowPoints = (filteredPoints) => {
      return filteredPoints.map(point => gmapCoords(point));

      // const projection = map.getProjection();
      // if (!projection) {
      //   return;
      // }
      // const scale = Math.pow(2, map.getZoom());
      // var pixelOffset = new mapApi.Point((-0.5/scale) || 0, (-0.5/scale) || 0);

      // // shift point helper
      // const shiftPoint = (point) => {
      //   const currPoint = projection.fromLatLngToPoint(gmapCoords(point));
      //   const newPoint = new mapApi.Point(
      //     currPoint.x - pixelOffset.x,
      //     currPoint.y - pixelOffset.y
      //   );
      //   return projection.fromPointToLatLng(newPoint);
      // }

      // return filteredPoints.map(point => shiftPoint(point));
    }


    // create track 
    if (mapApi && map && !elements.current) {
      // setElements(createTrack());
      elements.current = createTrack();
    }
    
    // update track
    if (mapApi && map && elements.current && doRedraw) {
      drawPath();
    }
  }, [
    mapApi, 
    map, 
    type, 
    points, 
    color, 
    isActive, 
    doRedraw, 
    zIndexBase, 
    strokeWeightBase,
    isActiveTrack,
  ]);


  // pin markers
  React.useEffect(() => {
    if (mapApi?.Marker && map) {
      const newPinMarkers = {};

      markers.forEach((marker) => {
        const isMarkerActive = marker.uuid === selectedMarkerUuid;
        let newPinMarker;
        const content = markerContent(
          type,
          marker.photoUrl ? MARKER_TYPE.PHOTO : MARKER_TYPE.MARKER,
          PIN_MARKER_COLOR,
          isMarkerActive ? 'large' : undefined,
        );

        if (pinMarkers.current[marker.uuid]) {
          // update existing marker
          pinMarkers.current[marker.uuid].content = content;
          pinMarkers.current[marker.uuid].zIndex = isMarkerActive ? 300 : zIndexBase + 4;
          newPinMarker = pinMarkers.current[marker.uuid];
          delete pinMarkers.current[marker.uuid]
        } else {
          // create new marker
          newPinMarker = new mapApi.Marker({
            position: {
              lat: marker.latitude,
              lng: marker.longitude,
            },
            map,
            zIndex: zIndexBase + 4,
            content: content,
          });
          newPinMarker.addListener("click", (e) => {
            selectMarker(marker.uuid);
          });
        }

        newPinMarkers[marker.uuid] = newPinMarker;
      });

      // any markers left in the old ref have been deleted
      for (const [, pinMarker] of Object.entries(pinMarkers.current)) {
        pinMarker.map = null;
      }

      pinMarkers.current = newPinMarkers;
    }
  }, [mapApi, map, markers, selectMarker, selectedMarkerUuid, type, zIndexBase]);


  // debug overlays
  React.useEffect(() => {
    if (displayedPointProperty) {
      if (debugMarkers.current.length !== points.length) {
        if (debugMarkers.current.length > 0) {
          // clear old markers
          debugMarkers.current.forEach(dm => {
            dm.marker.map = null;
          });
          debugMarkers.current = [];
        }

        // new render of debug markers
        points.forEach(point => {
          const pDiv = document.createElement('div');
          const pRoot = ReactDOM.createRoot(pDiv);
          pRoot.render(<DebugDetailMarker children={point[displayedPointProperty]} />);
          debugMarkers.current.push({
            pRoot,
            marker: new mapApi.Marker({
              map: isHidden ? null : map,
              title: 'point-overlay',
              content: pDiv,
              position: gmapCoords(point),
              zIndex: 100
            }),
          });
        });
      } else {
        // render existing more efficiently
        debugMarkers.current.forEach((dm, i) => {
          const point = points[i];
          dm.marker.map = isHidden ? null : map;
          dm.pRoot.render(<DebugDetailMarker children={point[displayedPointProperty]} />);
        });
      }
    } else {
      // no overlay
      debugMarkers.current.forEach(dm => {
        dm.pRoot.render(null);
      })
    }
  }, [displayedPointProperty, points, mapApi, map, isHidden]);


  // handles track visibility changes
  React.useEffect(() => {
    if (!elements.current || !map) {
      return;
    }

    setMap(isHidden ? null : map);
  }, [isHidden, map, setMap]);


  // clean up
  React.useEffect(() => {
    return () => {
      if (elements.current) {        
        setMap(null);

        // listeners
        if (elements.current.zoomListener) {
          mapApi.Event.removeListener(elements.current.zoomListener);
        }
        if (elements.current.clickListener) {
          mapApi.Event.removeListener(elements.current.clickListener);
        }
        
        debugMarkers.current = [];
        pinMarkers.current = {};
      }
    }
  }, [mapApi, setMap]);


  return (
    <>
    </>
  );
}